/**
 * Conflict Card Component
 * 
 * Displays a single conflict with:
 * - Severity badge and type label
 * - Title and description
 * - Details grid (date, appointments, etc.)
 * - Suggested solutions with confidence scores
 * - Action buttons (apply, resolve manually, ignore)
 */

import React, { useState } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import { 
  IConflict, 
  ISuggestedSolution, 
  IConflictDetail,
  ISolutionImpact
} from "../../types/conflictsDetector.types";
import styles from "../../appointmentsPage.module.css";

interface IConflictCardProps {
  conflict: IConflict;
  onApplySolution: (conflictId: string, solutionId: string) => void;
  onResolveManually: (conflictId: string) => void;
  onIgnore: (conflictId: string) => void;
}

const ConflictCard: React.FC<IConflictCardProps> = ({
  conflict,
  onApplySolution,
  onResolveManually,
  onIgnore
}) => {
  const [selectedSolution, setSelectedSolution] = useState<string | null>(null);
  
  const getSeverityClass = () => {
    switch (conflict.severity) {
      case 'critical': return styles.conflictCardCritical;
      case 'high': return styles.conflictCardHigh;
      case 'medium': return styles.conflictCardMedium;
      default: return '';
    }
  };

  const getSeverityTypeClass = () => {
    switch (conflict.severity) {
      case 'critical': return styles.conflictTypeCritical;
      case 'high': return styles.conflictTypeHigh;
      case 'medium': return styles.conflictTypeMedium;
      default: return '';
    }
  };

  const handleSolutionClick = (solutionId: string) => {
    setSelectedSolution(solutionId);
  };

  const handleApplyRecommended = () => {
    const recommended = conflict.suggestedSolutions.find(s => s.isRecommended);
    if (recommended) {
      onApplySolution(conflict.id, recommended.id);
    }
  };

  const hasRecommendedSolution = conflict.suggestedSolutions.some(s => s.isRecommended);

  return (
    <div className={`${styles.conflictCard} ${getSeverityClass()}`}>
      {/* Header */}
      <div className={styles.conflictHeader}>
        <div className={styles.conflictTitleSection}>
          <div className={`${styles.conflictType} ${getSeverityTypeClass()}`}>
            {t(conflict.typeLabel)}
          </div>
          <div className={styles.conflictTitle}>
            {t(conflict.title)}
          </div>
          <div className={styles.conflictDescription}>
            {t(conflict.description)}
          </div>
        </div>
      </div>

      {/* Details Grid */}
      <div className={styles.conflictDetails}>
        {conflict.details.map((detail: IConflictDetail, index: number) => (
          <div key={index} className={styles.conflictDetailItem}>
            <div className={styles.conflictDetailLabel}>{t(detail.label)}</div>
            <div 
              className={styles.conflictDetailValue}
              style={detail.highlight ? { 
                color: detail.highlight === 'danger' ? 'var(--danger)' : 
                       detail.highlight === 'success' ? 'var(--success)' : 
                       'var(--warning)' 
              } : undefined}
            >
              {detail.value}
            </div>
          </div>
        ))}
      </div>

      {/* Suggested Solutions */}
      {conflict.suggestedSolutions.length > 0 && (
        <div className={styles.conflictSolutionsSection}>
          <div className={styles.conflictSolutionsTitle}>
            <span>💡</span>
            <span>{t("CONFLICTS_SOLUTIONS_TITLE")}</span>
          </div>
          
          {conflict.suggestedSolutions.map((solution: ISuggestedSolution) => (
            <div 
              key={solution.id}
              className={`${styles.conflictSolutionCard} ${solution.isRecommended ? styles.conflictSolutionCardRecommended : ''} ${selectedSolution === solution.id ? styles.conflictSolutionCardSelected : ''}`}
              onClick={() => handleSolutionClick(solution.id)}
            >
              {solution.isRecommended && (
                <div className={styles.conflictSolutionRecommendedBadge}>
                  {t("CONFLICTS_SOLUTION_RECOMMENDED")}
                </div>
              )}
              <div className={styles.conflictSolutionHeader}>
                <div className={styles.conflictSolutionTitle}>
                  {t(solution.title)}
                </div>
                <div className={styles.conflictSolutionScore}>
                  {solution.score}%
                </div>
              </div>
              <div className={styles.conflictSolutionDescription}>
                {t(solution.description)}
              </div>
              <div className={styles.conflictSolutionImpacts}>
                {solution.impacts.map((impact: ISolutionImpact, idx: number) => (
                  <div key={idx} className={styles.conflictImpactTag}>
                    {t(impact.text)}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Action Buttons */}
      <div className={styles.conflictActions}>
        {hasRecommendedSolution && (
          <button 
            className={`${styles.conflictActionBtn} ${styles.conflictActionBtnPrimary}`}
            onClick={handleApplyRecommended}
          >
            {t("CONFLICTS_BTN_APPLY_RECOMMENDED")}
          </button>
        )}
        <button 
          className={`${styles.conflictActionBtn} ${styles.conflictActionBtnSecondary}`}
          onClick={() => onResolveManually(conflict.id)}
        >
          {t("CONFLICTS_BTN_RESOLVE_MANUALLY")}
        </button>
        <button 
          className={`${styles.conflictActionBtn} ${styles.conflictActionBtnSecondary}`}
          onClick={() => onIgnore(conflict.id)}
        >
          {t("CONFLICTS_BTN_IGNORE")}
        </button>
      </div>
    </div>
  );
};

export default ConflictCard;
